import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  getShopConfig,
  updateShopConfig,
  ShopConfigMissingError,
  type ShopConfig,
  type ShopConfigPatch,
} from '../api/shopConfig';

const MAX_SHIPPING_GROSZE = 100_000;

/**
 * The six shipping prices, as one list so the form, the draft and the patch cannot drift:
 * a row rendered without a matching patch key would look saved and never move the price.
 * Defaults track InPost's retail list. D is unpriced because no method ships it.
 */
const SHIPPING_PRICES = [
  { field: 'paczkomatAZl', savedKey: 'paczkomatCostAGrosze', patchKey: 'paczkomat_cost_a_grosze', method: 'paczkomat', size: 'A' },
  { field: 'paczkomatBZl', savedKey: 'paczkomatCostBGrosze', patchKey: 'paczkomat_cost_b_grosze', method: 'paczkomat', size: 'B' },
  { field: 'paczkomatCZl', savedKey: 'paczkomatCostCGrosze', patchKey: 'paczkomat_cost_c_grosze', method: 'paczkomat', size: 'C' },
  { field: 'kurierAZl', savedKey: 'kurierCostAGrosze', patchKey: 'kurier_cost_a_grosze', method: 'kurier', size: 'A' },
  { field: 'kurierBZl', savedKey: 'kurierCostBGrosze', patchKey: 'kurier_cost_b_grosze', method: 'kurier', size: 'B' },
  { field: 'kurierCZl', savedKey: 'kurierCostCGrosze', patchKey: 'kurier_cost_c_grosze', method: 'kurier', size: 'C' },
] as const satisfies ReadonlyArray<{
  field: keyof Draft;
  savedKey: keyof ShopConfig;
  patchKey: keyof ShopConfigPatch;
  method: 'paczkomat' | 'kurier';
  size: 'A' | 'B' | 'C';
}>;

const SIZE_HINT: Record<'A' | 'B' | 'C', string> = {
  A: '1-4 koszulki',
  B: '5-9 koszulek',
  C: '10+ koszulek',
};
const MIN_QUOTA = 1;
const MAX_QUOTA = 10;

type Draft = {
  shopOpen: boolean;
  closureMessage: string;
  estimatedShipDate: string;
  paczkomatAZl: string;
  paczkomatBZl: string;
  paczkomatCZl: string;
  kurierAZl: string;
  kurierBZl: string;
  kurierCZl: string;
  ticketShirtQuotaPerColor: string;
  boothAddress: string;
  courierDropoffPoint: string;
  concertDate: string;
};

type FieldErrors = Partial<Record<keyof Draft, string>>;

export function Settings() {
  const [saved, setSaved] = useState<ShopConfig | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');

  const load = async () => {
    try {
      const config = await getShopConfig();
      setSaved(config);
      setDraft(toDraft(config));
      setLoadError(null);
    } catch (err) {
      setLoadError(
        err instanceof ShopConfigMissingError
          ? 'Brak konfiguracji sklepu w bazie. Uruchom ponownie API, żeby ją utworzyć.'
          : describeError(err, 'Nie udało się wczytać ustawień.'),
      );
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const pending = useMemo(
    () => (saved && draft ? buildPatch(draft, saved) : null),
    [draft, saved],
  );

  if (loadError) return <div style={{ color: 'var(--admin-danger)' }}>{loadError}</div>;
  if (!draft || !saved || !pending) return <div className="muted">Ładuję ustawienia…</div>;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setSavedNotice(false);
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setDraft({ ...draft, [key]: value });
  };

  const changedCount = Object.keys(pending.patch).length;
  const hasEdits = changedCount > 0 || Object.keys(pending.errors).length > 0;

  const save = async () => {
    setSaveError(null);
    setSavedNotice(false);
    setErrors(pending.errors);
    if (Object.keys(pending.errors).length > 0) return;
    if (changedCount === 0) return;

    const payload: ShopConfigPatch = { ...pending.patch };
    const trimmedReason = reason.trim();
    if (trimmedReason) payload.reason = trimmedReason;

    setSaving(true);
    try {
      await updateShopConfig(payload);
      const fresh = await getShopConfig();
      setSaved(fresh);
      setDraft(toDraft(fresh));
      setReason('');
      setSavedNotice(true);
    } catch (err) {
      setSaveError(describeError(err, 'Nie udało się zapisać ustawień.'));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setDraft(toDraft(saved));
    setErrors({});
    setSaveError(null);
    setSavedNotice(false);
    setReason('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 820 }}>
      <Section
        title="Sklep"
        subtitle="Czy klienci mogą składać zamówienia"
        note={`Ostatnia zmiana: ${formatTimestamp(saved.updatedAt)}`}
      >
        <FieldRow label="Sklep otwarty">
          <Toggle
            checked={draft.shopOpen}
            onChange={(v) => set('shopOpen', v)}
            onLabel="Otwarty"
            offLabel="Zamknięty"
          />
        </FieldRow>
        <FieldRow
          label="Komunikat o zamknięciu"
          hint="Widoczny na stronie zamknięcia. Puste = sam nagłówek."
          error={errors.closureMessage}
        >
          <textarea
            className="input-a"
            rows={2}
            value={draft.closureMessage}
            onChange={(e) => set('closureMessage', e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </FieldRow>
      </Section>

      <Section
        title="Ceny wysyłki"
        subtitle="Cena zależy od gabarytu paczki"
        note={
          <>
            Gabaryt wynika z liczby koszulek: <b>1-4 → A</b>, <b>5-9 → B</b>, <b>10+ → C</b>. Cena
            jest liczona przy składaniu zamówienia i zapisywana na nim. Gabarytu D nie wysyłamy
            (paczkomat ani kurier), więc nie ma tu ceny. Ceny koszulek ustawiasz w{' '}
            <Link to="/admin/produkty">Produktach</Link>.
          </>
        }
      >
        <PriceMatrix draft={draft} errors={errors} set={set} />
      </Section>

      <Section
        title="Limity"
        subtitle="Ile koszulek każdego koloru przypada na jeden bilet"
        note="Limit działa przy składaniu zamówienia — złożone zamówienia zostają bez zmian. Zamówienia ręczne go nie podlegają."
      >
        <FieldRow
          label="Koszulek każdego koloru na bilet"
          hint="Przy wartości 1: jedna czarna i jedna biała na bilet, więc dwie koszulki tylko w dwóch różnych kolorach."
          error={errors.ticketShirtQuotaPerColor}
        >
          <input
            className="input-a mono"
            type="number"
            min={MIN_QUOTA}
            max={MAX_QUOTA}
            value={draft.ticketShirtQuotaPerColor}
            onChange={(e) => set('ticketShirtQuotaPerColor', e.target.value)}
            style={{ width: 90 }}
          />
        </FieldRow>
      </Section>

      <Section title="Terminy i odbiór" subtitle="Trafiają do maili i na dashboard">
        <FieldRow
          label="Przewidywana wysyłka"
          hint="Tekst wstawiany do maila po opłaceniu, np. „do 14 dni od zamknięcia zbiórki”."
          error={errors.estimatedShipDate}
        >
          <input
            className="input-a"
            value={draft.estimatedShipDate}
            onChange={(e) => set('estimatedShipDate', e.target.value)}
            style={{ width: '100%' }}
          />
        </FieldRow>
        <FieldRow label="Data koncertu" hint="Napędza odliczanie na dashboardzie." error={errors.concertDate}>
          <input
            className="input-a mono"
            type="date"
            value={draft.concertDate}
            onChange={(e) => set('concertDate', e.target.value)}
            style={{ width: 180 }}
          />
        </FieldRow>
        <FieldRow
          label="Miejsce odbioru"
          hint="Adres stoiska w mailu o odbiorze osobistym."
          error={errors.boothAddress}
        >
          <input
            className="input-a"
            value={draft.boothAddress}
            onChange={(e) => set('boothAddress', e.target.value)}
            style={{ width: '100%' }}
          />
        </FieldRow>
        <FieldRow
          label="Paczkomat nadawczy dla kuriera"
          hint="Kod paczkomatu, do którego wrzucacie paczki kurierskie (np. KRA01M). Kurier InPost odbiera je stamtąd i dowozi pod adres klienta. Punkt musi obsługiwać nadawanie kurierskie. Puste pole blokuje nadawanie kurierem."
          error={errors.courierDropoffPoint}
        >
          <input
            className="input-a"
            value={draft.courierDropoffPoint}
            onChange={(e) => set('courierDropoffPoint', e.target.value)}
            placeholder="KRA01M"
            style={{ width: '100%' }}
          />
        </FieldRow>
      </Section>

      <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FieldRow label="Powód zmiany" hint="Trafia do dziennika operacji. Opcjonalny.">
          <input
            className="input-a"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%' }}
          />
        </FieldRow>
        {saveError && <div style={{ color: 'var(--admin-danger)', fontSize: 12 }}>{saveError}</div>}
        {savedNotice && <div style={{ color: 'var(--admin-success)', fontSize: 12 }}>Zapisano.</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button className="btn-a primary" onClick={save} disabled={saving || !hasEdits}>
            {saving ? 'Zapisywanie…' : 'Zapisz ustawienia'}
          </button>
          <button className="btn-a" onClick={discard} disabled={saving || !hasEdits}>
            Odrzuć zmiany
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            {changedCount > 0 ? `Zmienione pola: ${changedCount}` : 'Brak zmian'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------- draft <-> config ----------

function toDraft(c: ShopConfig): Draft {
  return {
    shopOpen: c.shopOpen,
    closureMessage: c.closureMessage,
    estimatedShipDate: c.estimatedShipDate,
    paczkomatAZl: groszeToInput(c.paczkomatCostAGrosze),
    paczkomatBZl: groszeToInput(c.paczkomatCostBGrosze),
    paczkomatCZl: groszeToInput(c.paczkomatCostCGrosze),
    kurierAZl: groszeToInput(c.kurierCostAGrosze),
    kurierBZl: groszeToInput(c.kurierCostBGrosze),
    kurierCZl: groszeToInput(c.kurierCostCGrosze),
    ticketShirtQuotaPerColor: String(c.ticketShirtQuotaPerColor),
    boothAddress: c.boothAddress,
    courierDropoffPoint: c.courierDropoffPoint,
    concertDate: c.concertDate,
  };
}

const groszeToInput = (grosze: number) => (grosze / 100).toFixed(2);

function parseZlToGrosze(input: string): number | null {
  const normalized = input.trim().replace(',', '.');
  if (!/^\d+([.]\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

/**
 * Only fields the operator actually changed go into the payload. Two reasons: the endpoint logs
 * `shop.toggle` whenever `shop_open` is present — so an untouched toggle would forge toggle entries
 * in the audit log — and a narrow patch cannot clobber a field another operator edited meanwhile.
 */
function buildPatch(draft: Draft, saved: ShopConfig): { patch: ShopConfigPatch; errors: FieldErrors } {
  const patch: ShopConfigPatch = {};
  const errors: FieldErrors = {};

  if (draft.shopOpen !== saved.shopOpen) patch.shop_open = draft.shopOpen;
  if (draft.closureMessage !== saved.closureMessage) patch.closure_message = draft.closureMessage;

  const estimatedShipDate = draft.estimatedShipDate.trim();
  if (estimatedShipDate !== saved.estimatedShipDate) patch.estimated_ship_date = estimatedShipDate;

  for (const price of SHIPPING_PRICES) {
    const grosze = parseZlToGrosze(draft[price.field]);
    if (grosze === null || grosze > MAX_SHIPPING_GROSZE) {
      errors[price.field] = 'Kwota w zł, od 0 do 1000.';
    } else if (grosze !== saved[price.savedKey]) {
      patch[price.patchKey] = grosze;
    }
  }

  const quota = Number(draft.ticketShirtQuotaPerColor.trim());
  if (!Number.isInteger(quota) || quota < MIN_QUOTA || quota > MAX_QUOTA) {
    errors.ticketShirtQuotaPerColor = `Liczba całkowita od ${MIN_QUOTA} do ${MAX_QUOTA}.`;
  } else if (quota !== saved.ticketShirtQuotaPerColor) {
    patch.ticket_shirt_quota_per_color = quota;
  }

  const boothAddress = draft.boothAddress.trim();
  if (boothAddress !== saved.boothAddress) patch.booth_address = boothAddress;

  // Uppercased to match the locker codes InPost hands out, so a lowercase entry is not treated
  // as a different value and re-sent on every save.
  const courierDropoffPoint = draft.courierDropoffPoint.trim().toUpperCase();
  if (courierDropoffPoint !== saved.courierDropoffPoint) {
    patch.courier_dropoff_point = courierDropoffPoint;
  }

  // The dashboard countdown parses this as YYYY-MM-DD and silently shows no countdown otherwise.
  const concertDate = draft.concertDate.trim();
  if (concertDate && !/^\d{4}-\d{2}-\d{2}$/.test(concertDate)) {
    errors.concertDate = 'Format: RRRR-MM-DD.';
  } else if (concertDate !== saved.concertDate) {
    patch.concert_date = concertDate;
  }

  return { patch, errors };
}

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const code = err.body.error;
    if (code === 'invalid_courier_dropoff_point') {
      return `InPost nie zna paczkomatu ${String(err.body.code ?? '')} albo nie obsługuje on nadawania kurierskiego.`;
    }
    if (code === 'dropoff_validation_unavailable') {
      return 'Nie udało się sprawdzić paczkomatu w InPoście. Spróbuj ponownie za chwilę.';
    }
    const message = err.body.message;
    if (typeof message === 'string' && message) return message;
    if (err.status === 401) return 'Sesja wygasła. Zaloguj się ponownie.';
  }
  return fallback;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pl-PL');
}

// ---------- layout bits ----------

function Section({
  title,
  subtitle,
  note,
  children,
}: {
  title: string;
  subtitle?: string;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
        {subtitle?.toUpperCase()}
      </div>
      <h1 style={{ marginTop: 4, marginBottom: note ? 6 : 16 }}>{title}</h1>
      {note && (
        <div className="muted" style={{ fontSize: 12, marginBottom: 16 }}>
          {note}
        </div>
      )}
      <div style={{ display: 'grid', gap: 12 }}>{children}</div>
    </div>
  );
}

function FieldRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="settings-row">
      <label style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{label}</label>
      <div>
        {children}
        {hint && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {hint}
          </div>
        )}
        {error && <div style={{ color: 'var(--admin-danger)', fontSize: 11, marginTop: 4 }}>{error}</div>}
      </div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  onLabel,
  offLabel,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  onLabel: string;
  offLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={'btn-a' + (checked ? ' primary' : '')}
      onClick={() => onChange(!checked)}
      style={{ minWidth: 130, justifyContent: 'center' }}
    >
      {checked ? `● ${onLabel}` : `○ ${offLabel}`}
    </button>
  );
}

function PriceMatrix({
  draft,
  errors,
  set,
}: {
  draft: Draft;
  errors: FieldErrors;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '80px minmax(0, 1fr) minmax(0, 1fr)',
        gap: 10,
        alignItems: 'center',
      }}
    >
      <span />
      <span className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
        PACZKOMAT
      </span>
      <span className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
        KURIER
      </span>
      {(['A', 'B', 'C'] as const).map((size) => (
        <Fragment key={size}>
          <div>
            <div className="display" style={{ fontSize: 15 }}>
              GAB. {size}
            </div>
            <div className="muted" style={{ fontSize: 10 }}>
              {SIZE_HINT[size]}
            </div>
          </div>
          {(['paczkomat', 'kurier'] as const).map((method) => {
            const price = SHIPPING_PRICES.find((p) => p.method === method && p.size === size)!;
            return (
              <div key={method}>
                <input
                  className="input-a mono"
                  inputMode="decimal"
                  aria-label={`${method} gabaryt ${size} (zł)`}
                  value={draft[price.field]}
                  onChange={(e) => set(price.field, e.target.value)}
                  style={{ width: 110, maxWidth: '100%' }}
                />
                {errors[price.field] && (
                  <div style={{ color: 'var(--admin-danger)', fontSize: 11, marginTop: 4 }}>
                    {errors[price.field]}
                  </div>
                )}
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
